export type VideoSourceType = "vimeo" | "youtube" | "direct";

export interface VideoSource {
  type: VideoSourceType;
  id?: string;
  url: string;
}

/**
 * Parse a video source string into a structured VideoSource object.
 * Accepts: Vimeo ID, YouTube ID, full Vimeo/YouTube URLs, or direct video URLs.
 */
export function parseVideoSource(input: string): VideoSource {
  if (!input) return { type: "direct", url: "" };

  const trimmed = input.trim();

  // Pure digits → Vimeo ID
  if (/^\d+$/.test(trimmed)) {
    return { type: "vimeo", id: trimmed, url: getVimeoEmbedUrl(trimmed) };
  }

  // Try URL parsing
  try {
    const url = new URL(trimmed);

    // Vimeo URLs (player, manage, channels, showcases, live, etc.)
    if (url.hostname.includes("vimeo.com")) {
      // Unlisted videos require the privacy hash from the share URL or embed URL.
      const hash = url.searchParams.get("h") || url.pathname.match(/\/\d+\/([a-f0-9]+)(?:\/|$)/i)?.[1];
      // Try specific /videos/ID pattern first (handles review URLs with UUIDs)
      const videosMatch = url.pathname.match(/\/videos\/(\d+)/);
      if (videosMatch) {
        return { type: "vimeo", id: videosMatch[1], url: getVimeoEmbedUrl(videosMatch[1], { hash }) };
      }
      // Fallback to generic digit extraction
      const match = url.pathname.match(/(\d{5,})/);
      if (match) {
        return { type: "vimeo", id: match[1], url: getVimeoEmbedUrl(match[1], { hash }) };
      }
    }

    // YouTube URLs
    if (
      url.hostname.includes("youtube.com") ||
      url.hostname.includes("youtu.be")
    ) {
      let videoId: string | null = null;

      if (url.hostname.includes("youtu.be")) {
        videoId = url.pathname.slice(1);
      } else if (url.pathname.startsWith("/shorts/")) {
        videoId = url.pathname.replace("/shorts/", "");
      } else {
        videoId = url.searchParams.get("v");
      }

      if (videoId) {
        return { type: "youtube", id: videoId, url: getYouTubeEmbedUrl(videoId) };
      }
    }

    // Any other http(s) URL → direct video
    return { type: "direct", url: trimmed };
  } catch {
    // Not a URL — check if it looks like a YouTube ID (11 alphanumeric + dash/underscore chars)
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
      return { type: "youtube", id: trimmed, url: getYouTubeEmbedUrl(trimmed) };
    }

    // Fallback to direct
    return { type: "direct", url: trimmed };
  }
}

export interface EmbedOptions {
  hash?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  background?: boolean;
  quality?: string;
}

const defaultOptions: EmbedOptions = {
  autoplay: true,
  loop: true,
  muted: true,
  background: true,
};

export function getVimeoEmbedUrl(id: string, options: EmbedOptions = {}): string {
  const opts = { ...defaultOptions, ...options };
  const params = new URLSearchParams();
  if (opts.hash) params.set("h", opts.hash);
  if (opts.autoplay) params.set("autoplay", "1");
  if (opts.loop) params.set("loop", "1");
  if (opts.muted) params.set("muted", "1");
  if (opts.background) params.set("background", "1");
  if (opts.quality) params.set("quality", opts.quality);
  params.set("dnt", "1");
  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

export function getYouTubeEmbedUrl(id: string, options: EmbedOptions = {}): string {
  const opts = { ...defaultOptions, ...options };
  const params = new URLSearchParams();
  if (opts.autoplay) params.set("autoplay", "1");
  if (opts.muted) params.set("mute", "1");
  if (opts.loop) {
    params.set("loop", "1");
    params.set("playlist", id); // Required for YouTube loop
  }
  if (opts.background) {
    params.set("controls", "0");
    params.set("showinfo", "0");
    params.set("rel", "0");
    params.set("modestbranding", "1");
  }
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

